import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../services/API";

// Bug 1 fixed: thunk now accepts { sort, nextUrl, isLoadMore } to support pagination
// Bug 4 fixed: no longer returns raw payload — wraps in { data, isLoadMore } for reducer
export const yourCommentActivity = createAsyncThunk(
    "yourComment", async ({ sort, nextUrl, isLoadMore = false } = {}, { rejectWithValue }) => {
        try {
            let url;
            if (nextUrl) {
                const urlObj = new URL(nextUrl);
                const pathAndQuery = urlObj.pathname + urlObj.search;
                url = pathAndQuery.replace('/api/', '');
            } else {
                url = `post/user/activity/comments/sorted/?sort=${sort}`;
            }
            const response = await axiosInstance.get(url);
            return { data: response.data, isLoadMore };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Fetch watch history
export const fetchWatchHistory = createAsyncThunk(
    "activity/fetchWatchHistory",
    async ({ nextUrl, isLoadMore } = {}, { rejectWithValue }) => {
        try {
            let url;
            if (nextUrl) {
                const urlObj = new URL(nextUrl);
                const pathAndQuery = urlObj.pathname + urlObj.search;
                url = pathAndQuery.replace('/api/', '');
            } else {
                url = "post/watched-history/";
            }
            const response = await axiosInstance.get(url);
            return { ...response.data, isLoadMore };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Clear watch history
export const clearWatchHistory = createAsyncThunk(
    "activity/clearWatchHistory",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete("post/watched-history/clear/");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const YourActivity = createSlice({
    name: "YourActivity",
    initialState: {
        commentData: null,          // Bug 4 fixed: was [] — should be null (it's an object after load)
        commentNext: null,          // Bug 3 added: tracks next page URL for comment pagination
        commentLoadingMore: false,  // Bug 3 added: spinner state for loading more comments
        loading: false,
        error: null,
        // Watch history state
        watchedVideos: [],
        totalWatchedCount: 0,
        count: 0,
        next: null,
        previous: null,
        watchHistoryLoading: false,
        watchHistoryLoadingMore: false,
        watchHistoryError: null,
        clearLoading: false,
        clearError: null,
    },
    extraReducers: (builder) => {
        // Your comment activity
        builder.addCase(yourCommentActivity.pending, (state, action) => {
            // Bug 3 fixed: distinguish initial load from load-more
            if (action.meta.arg?.isLoadMore) {
                state.commentLoadingMore = true;
            } else {
                state.loading = true;
            }
            state.error = null;
        });
        builder.addCase(yourCommentActivity.fulfilled, (state, action) => {
            state.loading = false;
            state.commentLoadingMore = false;
            const { data, isLoadMore } = action.payload;
            // Bug 3 fixed: append on load-more, replace on fresh fetch
            if (isLoadMore) {
                const existing = state.commentData?.results?.comments || [];
                const incoming = data?.results?.comments || [];
                state.commentData = {
                    ...data,
                    results: { ...data.results, comments: [...existing, ...incoming] },
                };
            } else {
                state.commentData = data;
            }
            state.commentNext = data?.next || null;
        });
        builder.addCase(yourCommentActivity.rejected, (state, action) => {
            state.loading = false;
            state.commentLoadingMore = false;
            // Bug 1 fixed: was action.error.message ("Rejected") — now uses rejectWithValue payload
            state.error = action.payload || action.error.message;
        });

        // Fetch watch history
        builder.addCase(fetchWatchHistory.pending, (state, action) => {
            if (action.meta.arg?.isLoadMore) {
                state.watchHistoryLoadingMore = true;
            } else {
                state.watchHistoryLoading = true;
            }
            state.watchHistoryError = null;
        });
        builder.addCase(fetchWatchHistory.fulfilled, (state, action) => {
            state.watchHistoryLoading = false;
            state.watchHistoryLoadingMore = false;

            if (action.payload.isLoadMore) {
                state.watchedVideos = [...state.watchedVideos, ...(action.payload.results?.watched_videos || [])];
            } else {
                state.watchedVideos = action.payload.results?.watched_videos || [];
            }

            state.totalWatchedCount = action.payload.results?.total_watched_count || 0;
            state.count = action.payload.count || 0;
            state.next = action.payload.next;
            state.previous = action.payload.previous;
        });
        builder.addCase(fetchWatchHistory.rejected, (state, action) => {
            state.watchHistoryLoading = false;
            state.watchHistoryLoadingMore = false;
            state.watchHistoryError = action.payload || action.error.message;
        });

        // Clear watch history
        builder.addCase(clearWatchHistory.pending, (state) => {
            state.clearLoading = true;
            state.clearError = null;
        });
        builder.addCase(clearWatchHistory.fulfilled, (state) => {
            state.clearLoading = false;
            state.watchedVideos = [];
            state.totalWatchedCount = 0;
            state.count = 0;
        });
        builder.addCase(clearWatchHistory.rejected, (state, action) => {
            state.clearLoading = false;
            state.clearError = action.payload || action.error.message;
        });
    },
});

export default YourActivity.reducer;
